import './assets/main.css'
import { createApp } from 'vue'
import { createPinia } from 'pinia'

import ui from '@nuxt/ui/vue-plugin'
import App from './App.vue'
import router from './router'
import axios, { type AxiosRequestConfig } from 'axios'
import { redirectToLogin } from '@/utils/auth-redirect'
import { createAuthErrorHandler, SESSION_REFRESH_URL } from '@/utils/auth-interceptor'
// Imported for its side effect: applies the persisted/system theme to <html>
// synchronously, before app.mount(), avoiding a flash of the wrong theme.
import '@/composables/useTheme'

// Request-Interceptor für CSRF und Cookies
axios.interceptors.request.use((config) => {
  axios.defaults.withCredentials = true
  return config
})

// Globaler Axios-Interceptor für Authentifizierungsfehler: bei 401 erst still die Sitzung
// erneuern und den Request wiederholen, sonst zum Login leiten.
const handleAuthError = createAuthErrorHandler({
  request: (config) => axios.request(config as AxiosRequestConfig),
  refresh: () => axios.post(SESSION_REFRESH_URL),
  redirectToLogin: () => redirectToLogin(),
})
axios.interceptors.response.use((response) => response, handleAuthError)

// Nach einem Deploy verweisen bereits geladene Seiten noch auf alte, gehashte Chunk-Dateien,
// die auf dem Server nicht mehr existieren (404). Vite meldet das als `vite:preloadError`.
// In diesem Fall laden wir die Seite einmalig neu, damit der Browser die aktuellen Chunks holt.
window.addEventListener('vite:preloadError', () => {
  const reloadedFlag = 'chunk-reload-attempted'
  if (sessionStorage.getItem(reloadedFlag)) {
    return
  }
  sessionStorage.setItem(reloadedFlag, '1')
  window.location.reload()
})

const app = createApp(App)

interface ObservedElement extends HTMLElement {
  _observer?: IntersectionObserver
}

app.directive('intersection-observer', {
  mounted(el: ObservedElement, binding) {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        binding.value()
      }
    })
    observer.observe(el)
    el._observer = observer
  },
  unmounted(el: ObservedElement) {
    if (el._observer) {
      el._observer.disconnect()
    }
  },
})

app.use(createPinia())
app.use(router)
app.use(ui)

app.mount('#app')

// App erfolgreich gemountet: Reload-Guard zurücksetzen, damit ein späterer Deploy
// wieder einen einmaligen automatischen Reload auslösen kann.
sessionStorage.removeItem('chunk-reload-attempted')
